import React, { useState } from 'react'

import { TKeyMetric } from 'types/GlobalKeyMetric'
import { RollingType } from 'types/GlobalKpiOption'
import {
  isCustomEconomyMetric,
  ShowPerEnum,
  TCustomizedMetric
} from 'types/GlobalMetric'

import { useTranslation } from 'language/translator'
import { useAccountSettings } from 'redux/hooks/SignedInAccount'

import Box from 'components_new/atoms/Box'
import Button from 'components_new/atoms/Button'
import InputAdornment from 'components_new/atoms/InputAdornment'
import Link from 'components_new/atoms/Link'
import Switch from 'components_new/atoms/Switch'
import TextField from 'components_new/atoms/TextField'

import DisabledWrapper from 'components_new/molecules/DisabledWrapper'
import FormSection from 'components_new/molecules/FormSection'
import SectionHeader from 'components_new/molecules/SectionHeader'
import SelectSearch from 'components_new/molecules/SelectSearch'
import SimpleDialog from 'components_new/molecules/SimpleDialog'
import StatusBox from 'components_new/molecules/StatusBox'
import TrendTag from 'components_new/molecules/TrendTag'

import EntitySlices from 'components_new/organisms/EntitySlices'
import MetricPreview from 'components_new/organisms/MetricPreview'

import { useMetricEditOptions } from '../dialogs/MetricEditDialog/utils'

interface Props {
  displayPreview?: boolean
  editKeyMetric: TKeyMetric | null
  editMetric: TCustomizedMetric
  onDistributionChange: (value: { id: ShowPerEnum } | null) => void
  onKeyMetricChange: (update: Partial<TKeyMetric>) => void
  onMetricChange: (value: { id: string }) => void
  onRollingSumChange: (value: { id: RollingType } | null) => void
  onSlicesChange: (value: { id: string }[]) => void
}

const MetricEditBody = (props: Props) => {
  const {
    displayPreview,
    editKeyMetric,
    editMetric,
    onDistributionChange,
    onKeyMetricChange,
    onMetricChange,
    onRollingSumChange,
    onSlicesChange
  } = props

  const { isAdmin } = useAccountSettings()
  const { t } = useTranslation()

  const [editSlices, setEditSlices] = useState(false)

  const {
    distributionOptions,
    fullMetric,
    metricOptions,
    rollingSumOptions,
    selectedDistributionOption,
    selectedMetricOption,
    selectedRollingSumOption,
    selectedSliceOptions,
    sliceOptions
  } = useMetricEditOptions(editMetric)

  const isEconomicalMetric = isCustomEconomyMetric(fullMetric)

  return (
    <>
      {displayPreview ? (
        <MetricPreview
          id={editMetric.id}
          keyMetric={editKeyMetric ?? undefined}
          settings={editMetric.settings}
          sx={{ mb: 2 }}
        />
      ) : null}
      <SectionHeader divider gutterBottom title={t('global', 'settings')} />
      {editKeyMetric ? (
        <FormSection size="small" title={t('global', 'title')}>
          <TextField
            noGutter
            onChange={(e) => onKeyMetricChange({ title: e.target.value })}
            size="small"
            value={editKeyMetric.title}
          />
        </FormSection>
      ) : null}
      <FormSection size="small" title={t('metrics', 'metric')}>
        <SelectSearch
          closeOnSelect
          disableClearable
          multiple={false}
          noGutter
          onChange={onMetricChange}
          options={metricOptions}
          selected={selectedMetricOption}
        />
      </FormSection>
      <Box sx={{ display: 'flex', gap: 2 }}>
        <FormSection
          size="small"
          sx={{ flex: 1 }}
          title={t('metrics', 'rolling_sum')}
        >
          <SelectSearch
            closeOnSelect
            disabled={rollingSumOptions.length === 0}
            multiple={false}
            noGutter
            onChange={onRollingSumChange}
            options={rollingSumOptions}
            selected={selectedRollingSumOption}
          />
        </FormSection>

        <FormSection
          size="small"
          sx={{ flex: 1 }}
          title={t('metrics', 'distribution')}
        >
          <SelectSearch
            closeOnSelect
            disabled={distributionOptions.length === 0}
            multiple={false}
            noGutter
            onChange={onDistributionChange}
            options={distributionOptions}
            selected={selectedDistributionOption}
          />
        </FormSection>
      </Box>

      <FormSection
        actions={
          isAdmin ? (
            <Link
              onClick={(e) => {
                e.stopPropagation()
                setEditSlices(true)
              }}
              variant="caption"
            >
              {t('global', 'edit')}
            </Link>
          ) : null
        }
        size="small"
        title={t('entities', 'slices')}
      >
        <SelectSearch
          closeOnSelect
          disableClearable
          disabled={sliceOptions.length === 0}
          multiple
          noGutter
          onChange={onSlicesChange}
          options={sliceOptions}
          selected={selectedSliceOptions}
        />
      </FormSection>
      {editKeyMetric ? (
        <>
          <FormSection
            actions={
              <>
                <TrendTag
                  increaseIsPositive={editKeyMetric.increase_is_positive}
                  value={10}
                />
                <TrendTag
                  increaseIsPositive={editKeyMetric.increase_is_positive}
                  value={-10}
                />
              </>
            }
            size="small"
            title={t('widgets', 'increase_is')}
          >
            <SelectSearch
              closeOnSelect
              disableClearable
              multiple={false}
              noGutter
              onChange={(value) =>
                onKeyMetricChange({
                  increase_is_positive: value.id === 'true'
                })
              }
              options={[
                { id: 'true', title: t('widgets', 'positive') },
                { id: 'false', title: t('widgets', 'negative') }
              ]}
              selected={
                editKeyMetric.increase_is_positive
                  ? { id: 'true', title: t('widgets', 'positive') }
                  : { id: 'false', title: t('widgets', 'negative') }
              }
            />
          </FormSection>
          <FormSection
            actions={
              <Switch
                checked={editKeyMetric.target_activated ?? false}
                onChange={() => {
                  const newActivated = !editKeyMetric.target_activated

                  onKeyMetricChange({
                    target_activated: newActivated,
                    ...(isEconomicalMetric ? { target_value: null } : {})
                  })
                }}
                size="small"
              />
            }
            size="small"
            title={t('key_metrics', 'target')}
          >
            <DisabledWrapper disabled={!editKeyMetric.target_activated}>
              {isEconomicalMetric ? (
                <StatusBox
                  severity="info"
                  title={t('key_metrics', 'budget_target_title')}
                >
                  {t('key_metrics', 'budget_target_body')}
                </StatusBox>
              ) : (
                <TextField
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        {fullMetric?.unit}
                      </InputAdornment>
                    )
                  }}
                  noGutter
                  onChange={(e) => {
                    const val = e.target.value

                    onKeyMetricChange({
                      target_value: val === '' ? null : Number(val)
                    })
                  }}
                  size="small"
                  type="number"
                  value={editKeyMetric.target_value ?? ''}
                />
              )}
            </DisabledWrapper>
          </FormSection>
        </>
      ) : null}
      {fullMetric && fullMetric.entity ? (
        <SimpleDialog
          actions={
            <Button onClick={() => setEditSlices(false)} variant="text">
              {t('global', 'close')}
            </Button>
          }
          onClose={() => setEditSlices(false)}
          open={editSlices}
          sx={{ zIndex: 1302 }}
          title={t('entities', 'slices')}
        >
          <EntitySlices edit id={fullMetric.entity.id} />
        </SimpleDialog>
      ) : null}
    </>
  )
}

export default MetricEditBody
